import { ArrowRight, Star, ShieldCheck, Sparkles, Clock } from "lucide-react";
import { heroFeatures, heroImage, trustBar } from "../mock";
import * as Icons from "lucide-react";

const iconMap = { ShieldCheck, Sparkles, Clock };

const Hero = () => {
  return (
    <section className="relative overflow-hidden pt-[74px]">
      {/* subtle background gradient accents */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-40 right-0 w-[720px] h-[720px] rounded-full bg-blue-100/60 blur-3xl" />
        <div className="absolute top-40 -left-40 w-[520px] h-[520px] rounded-full bg-sky-50 blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-10 items-center py-14 lg:py-24">
          {/* Left */}
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-100 px-4 py-1.5 text-[13px] font-semibold text-blue-700">
              <span className="w-2 h-2 rounded-full bg-blue-600" />
              Ihr Partner für professionelle Gebäudereinigung
            </div>

            <h1 className="font-display mt-6 text-slate-900 tracking-tight leading-[1.05] text-[32px] sm:text-[48px] lg:text-[64px] break-words" style={{ fontWeight: 900 }}>
              Glänzende <span className="text-blue-600">Sauberkeit</span> für Ihr
              Unternehmen in Augsburg.
            </h1>

            <p className="mt-6 text-[16px] sm:text-[17px] leading-relaxed text-slate-600 max-w-xl">
              Zuverlässig. Gründlich. Professionell. MAG Facility Services UG aus
              Augsburg sorgt für makellose Sauberkeit in Büros, Praxen,
              Treppenhäusern und Gewerbeobjekten – zertifiziert, versichert und
              pünktlich.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3 sm:gap-4">
              <a
                href="#kontakt"
                className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-blue-600 text-white text-[15px] font-semibold shadow-xl shadow-blue-600/25 hover:bg-blue-700 transition-colors"
              >
                Kostenloses Angebot anfordern
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#leistungen"
                className="inline-flex items-center justify-center px-6 py-3.5 rounded-lg border border-slate-300 text-slate-800 text-[15px] font-semibold hover:border-slate-400 hover:bg-slate-50 transition-colors"
              >
                Unsere Leistungen
              </a>
            </div>

            <div className="mt-10 sm:mt-12 grid grid-cols-3 gap-3 sm:gap-6 max-w-lg">
              {heroFeatures.map((f) => {
                const Icon = iconMap[f.icon];
                return (
                  <div key={f.title} className="flex items-start gap-2 sm:gap-2.5">
                    <Icon className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" strokeWidth={2.2} />
                    <div>
                      <div className="text-[14px] sm:text-[15px] font-bold text-slate-900 leading-tight">
                        {f.title}
                      </div>
                      <div className="text-[12px] sm:text-[13px] text-slate-500 leading-tight">{f.subtitle}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right image */}
          <div className="relative animate-fade-up mb-8 lg:mb-0" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-slate-300/60 ring-1 ring-slate-200/60">
              <img
                src={heroImage}
                alt="Fensterreinigung in Augsburg – Reinigungskraft von MAG Facility Services reinigt eine Glasscheibe mit Abzieher"
                width="900"
                height="600"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full h-[300px] sm:h-[440px] lg:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
            </div>

            {/* floating rating card */}
            <div className="absolute -bottom-6 left-4 right-4 sm:left-6 sm:right-16 md:right-24 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 flex items-center gap-3 sm:gap-4 animate-float">
              <div className="flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-blue-600 text-white shrink-0">
                <Star className="w-5 h-5 fill-white" />
              </div>
              <div>
                <div className="text-[14px] sm:text-[15px] font-bold text-slate-900 leading-snug">
                  5,0 / 5,0 Kundenzufriedenheit
                </div>
                <div className="text-[12px] sm:text-[13px] text-slate-500">
                  Von unseren Kunden bestens bewertet
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trust bar */}
      <div className="border-y border-slate-100 bg-white/60">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6">
          <div className="flex flex-wrap items-center justify-center lg:justify-between gap-x-8 gap-y-4">
            {trustBar.map((t) => {
              const Icon = Icons[t.icon] || ShieldCheck;
              return (
                <div key={t.label} className="flex items-center gap-2.5 text-slate-600">
                  <Icon className="w-5 h-5 text-blue-600" strokeWidth={2.1} />
                  <span className="text-[14px] font-medium">{t.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
